import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Fuse from 'fuse.js';
import {
  Close as CloseIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  getPlaybookLibraryItems,
  type PlaybookLibraryItem,
  type PlaybookLibraryType
} from '../services/apiService';

const TYPE_LABELS: Record<PlaybookLibraryType, string> = {
  pitch: 'Intro Pitch',
  question: 'Qualifying Questions',
  advanced: 'Deal Progression'
};

interface LibrarySelectPanelProps {
  type: PlaybookLibraryType;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (texts: string[]) => void;
}

const LibrarySelectPanel: React.FC<LibrarySelectPanelProps> = ({
  type,
  isOpen,
  onClose,
  onSelect
}) => {
  const [allItems, setAllItems] = useState<PlaybookLibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPlaybookLibraryItems(type, 1, 500);
      setAllItems(response?.items || []);
    } catch (error) {
      console.error('Error fetching library items:', error);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      setSearchQuery('');
      fetchItems();
    }
  }, [isOpen, fetchItems]);

  const fuse = useMemo(
    () => new Fuse(allItems, { keys: ['text'], threshold: 0.4 }),
    [allItems]
  );

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return allItems;
    return fuse.search(searchQuery).map(result => result.item);
  }, [searchQuery, allItems, fuse]);

  const toggleItem = (id: string) => {
    const updated = new Set(selectedIds);
    if (updated.has(id)) {
      updated.delete(id);
    } else {
      updated.add(id);
    }
    setSelectedIds(updated);
  };

  const handleAddSelected = () => {
    const selectedTexts = allItems
      .filter(item => selectedIds.has(item._id))
      .map(item => item.text);

    if (selectedTexts.length > 0) {
      onSelect(selectedTexts);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-4 pb-3 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                Select from Library - {TYPE_LABELS[type]}
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Search */}
            <div className="mt-3 relative">
              <SearchIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                style={{ fontSize: '1.25rem' }}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search items..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Items list */}
          <div className="px-4 py-3 max-h-[50vh] overflow-y-auto">
            {loading ? (
              <p className="text-center text-gray-500 py-4">Loading...</p>
            ) : allItems.length === 0 ? (
              <p className="text-center text-gray-400 py-4 text-sm">
                No {TYPE_LABELS[type].toLowerCase()} items in library.
                Add items in the Library tab first.
              </p>
            ) : filteredItems.length === 0 ? (
              <p className="text-center text-gray-400 py-4 text-sm">
                No items match your search.
              </p>
            ) : (
              <div className="space-y-1">
                {filteredItems.map(item => (
                  <label
                    key={item._id}
                    className="flex items-start gap-3 p-2 rounded-md hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item._id)}
                      onChange={() => toggleItem(item._id)}
                      className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{item.text}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 border-t flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {selectedIds.size} selected
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddSelected}
                disabled={selectedIds.size === 0}
                className={`px-4 py-2 text-white rounded-md text-sm ${
                  selectedIds.size === 0
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                Add Selected ({selectedIds.size})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LibrarySelectPanel;
